import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPartner } from '../api';

const inputClass =
  'w-full rounded-lg border border-leather bg-leather/30 px-3 py-2 text-white placeholder-station-muted focus:ring-2 focus:ring-gold focus:border-gold';
const labelClass = 'block text-sm font-medium text-gold/90 mb-1';

export default function AddPartner() {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [tier, setTier] = useState<'Associate' | 'Certified Broker'>('Associate');
  const [stateTerritory, setStateTerritory] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !email.trim() || !stateTerritory.trim()) {
      setError('Name, email, and territory are required.');
      return;
    }
    setSubmitting(true);
    try {
      const partner = await createPartner({
        name: name.trim(),
        email: email.trim(),
        tier,
        state_territory: stateTerritory.trim(),
        ...(password.trim() && { password: password.trim() }),
      });
      navigate(`/partners/${partner.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create partner');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-xl">
      <h1 className="text-2xl font-semibold text-gold">Add partner</h1>
      <p className="text-station-muted mt-1 mb-6">Create a new territory partner.</p>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="rounded-lg bg-red-900/40 border border-red-500/50 text-red-200 text-sm px-4 py-3">
            {error}
          </div>
        )}
        <div>
          <label className={labelClass}>Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputClass}
            required
          />
        </div>
        <div>
          <label className={labelClass}>Email *</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
            required
          />
        </div>
        <div>
          <label className={labelClass}>Tier *</label>
          <select
            value={tier}
            onChange={(e) => setTier(e.target.value as 'Associate' | 'Certified Broker')}
            className={inputClass}
          >
            <option value="Associate">Associate (3%)</option>
            <option value="Certified Broker">Certified Broker (5%)</option>
          </select>
        </div>
        <div>
          <label className={labelClass}>Portal password (optional)</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputClass}
            placeholder="Partner uses this to sign in to their portal"
            autoComplete="new-password"
          />
        </div>
        <div>
          <label className={labelClass}>State / Territory *</label>
          <input
            type="text"
            value={stateTerritory}
            onChange={(e) => setStateTerritory(e.target.value)}
            className={inputClass}
            placeholder="e.g. CA, Southwest"
            required
          />
        </div>
        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-2 rounded-lg bg-gold text-charcoal font-medium hover:bg-gold-light disabled:opacity-50 transition-colors"
          >
            {submitting ? 'Creating…' : 'Create partner'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/')}
            className="px-4 py-2 rounded-lg border border-leather text-station-muted hover:bg-leather/30 hover:text-white transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
